import { buildMaterialCode } from "@edm/domain";
import { hasPermission } from "@edm/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { CsvImportDialog } from "@/components/csv-import-dialog";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApiError } from "@/lib/api";
import { useMe } from "@/lib/auth";
import {
  createResource,
  listResource,
  type MaterialRow,
  updateResource,
  useReferenceData,
} from "@/lib/master-data";

interface MaterialForm {
  name: string;
  levelId: string;
  categoryId: string;
  productId: string;
  specConfigId: string;
  unit: string;
  price: string;
  lifeDays: string;
  companyId: string;
  overrideReason: string;
}

const EMPTY_FORM: MaterialForm = {
  name: "",
  levelId: "",
  categoryId: "",
  productId: "",
  specConfigId: "",
  unit: "个",
  price: "",
  lifeDays: "365",
  companyId: "",
  overrideReason: "",
};

function text(value: string): string {
  return value.trim();
}

export function MaterialsPage() {
  const { data: me } = useMe();
  const reference = useReferenceData();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<"materials" | "taxonomy">("materials");
  const [query, setQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState<MaterialForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<MaterialRow | null>(null);
  const [editForm, setEditForm] = useState({
    name: "",
    unit: "个",
    price: "",
    lifeDays: "365",
    companyId: "",
    overrideReason: "",
  });
  const [categoryName, setCategoryName] = useState("");
  const [productName, setProductName] = useState("");
  const [specName, setSpecName] = useState("");
  const [taxonomyLevelId, setTaxonomyLevelId] = useState("");
  const [taxonomyCategoryId, setTaxonomyCategoryId] = useState("");
  const [taxonomyProductId, setTaxonomyProductId] = useState("");

  const canManage = Boolean(me && hasPermission(me.permissions, "material:manage"));
  const canTaxonomy = Boolean(me && hasPermission(me.permissions, "taxonomy:manage"));
  const canOverride = Boolean(
    me && hasPermission(me.permissions, "material:classification:override"),
  );

  const materialsQuery = useQuery({
    queryKey: ["materials"],
    queryFn: () => listResource<MaterialRow>("/api/v1/materials"),
  });

  const levels = reference.data?.materialLevels ?? [];
  const categories = reference.data?.materialCategories ?? [];
  const products = reference.data?.materialProducts ?? [];
  const specs = reference.data?.materialSpecConfigs ?? [];
  const companies = reference.data?.companies ?? [];

  const levelById = useMemo(() => new Map(levels.map((item) => [item.id, item])), [levels]);
  const categoryById = useMemo(
    () => new Map(categories.map((item) => [item.id, item])),
    [categories],
  );
  const productById = useMemo(() => new Map(products.map((item) => [item.id, item])), [products]);
  const specById = useMemo(() => new Map(specs.map((item) => [item.id, item])), [specs]);

  const filtered = useMemo(() => {
    const rows = materialsQuery.data ?? [];
    if (!query.trim()) return rows;
    const keyword = query.trim().toLowerCase();
    return rows.filter((row) => `${row.code} ${row.name}`.toLowerCase().includes(keyword));
  }, [materialsQuery.data, query]);

  const formLevel = levelById.get(form.levelId);
  const formCategory = categoryById.get(form.categoryId);
  const formProduct = productById.get(form.productId);
  const formSpec = specById.get(form.specConfigId);
  const previewCode =
    formLevel && formCategory && formProduct
      ? (() => {
          try {
            return buildMaterialCode({
              levelCode: formLevel.code,
              categoryCode: formCategory.code,
              productCode: formProduct.code,
              specCode: formSpec?.code ?? null,
            });
          } catch {
            return "";
          }
        })()
      : "";

  const formCategories = categories.filter(
    (item) => item.levelId === form.levelId && item.isActive,
  );
  const formProducts = products.filter(
    (item) => item.categoryId === form.categoryId && item.isActive,
  );
  const formSpecs = specs.filter((item) => item.productId === form.productId && item.isActive);

  function openCreate() {
    const firstLevel = levels[0];
    setForm({
      ...EMPTY_FORM,
      levelId: firstLevel?.id ?? "",
      companyId: companies[0]?.id ?? "",
    });
    setDialogOpen(true);
  }

  function setField<K extends keyof MaterialForm>(key: K, value: MaterialForm[K]) {
    setForm((current) => {
      const next = { ...current, [key]: value };
      if (key === "levelId") {
        next.categoryId = "";
        next.productId = "";
        next.specConfigId = "";
      }
      if (key === "categoryId") {
        next.productId = "";
        next.specConfigId = "";
      }
      if (key === "productId") {
        next.specConfigId = "";
      }
      return next;
    });
  }

  async function createMaterial() {
    if (!text(form.name)) return toast.error("请输入物料名称");
    if (!form.levelId) return toast.error("请选择物料级别");
    if (!form.categoryId) return toast.error("请选择物料类别");
    if (!form.productId) return toast.error("请选择品名");
    if (formLevel?.code === 1 && !form.specConfigId)
      return toast.error("资产类物料必须选择规格配置");
    if (!text(form.lifeDays) || Number(form.lifeDays) <= 0)
      return toast.error("预计使用期必须大于 0");
    setSaving(true);
    try {
      await createResource("/api/v1/materials", {
        name: text(form.name),
        levelId: form.levelId,
        categoryId: form.categoryId,
        productId: form.productId,
        specConfigId: form.specConfigId || null,
        unit: text(form.unit) || "个",
        estimatedUnitPriceYuan: text(form.price) ? Number(form.price) : null,
        expectedLifeDays: Number(form.lifeDays),
        companyId: form.companyId || null,
        ...(form.overrideReason.trim()
          ? { classificationOverrideReason: form.overrideReason.trim() }
          : {}),
      });
      toast.success(`物料已创建${previewCode ? `：${previewCode}` : ""}`);
      setDialogOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["materials"] }),
        queryClient.invalidateQueries({ queryKey: ["reference-data"] }),
      ]);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "物料创建失败，请稍后重试");
    } finally {
      setSaving(false);
    }
  }

  async function addTaxonomy(kind: "category" | "product" | "spec") {
    try {
      if (kind === "category") {
        if (!taxonomyLevelId || !categoryName.trim())
          return toast.error("请选择级别并填写类别名称");
        await createResource("/api/v1/admin/material-taxonomy/categories", {
          levelId: taxonomyLevelId,
          name: categoryName.trim(),
        });
        setCategoryName("");
      } else if (kind === "product") {
        if (!taxonomyCategoryId || !productName.trim())
          return toast.error("请选择类别并填写品名名称");
        await createResource("/api/v1/admin/material-taxonomy/products", {
          categoryId: taxonomyCategoryId,
          name: productName.trim(),
        });
        setProductName("");
      } else {
        if (!taxonomyProductId || !specName.trim()) return toast.error("请选择品名并填写规格名称");
        await createResource("/api/v1/admin/material-taxonomy/spec-configs", {
          productId: taxonomyProductId,
          name: specName.trim(),
        });
        setSpecName("");
      }
      toast.success("层级已新增");
      await queryClient.invalidateQueries({ queryKey: ["reference-data"] });
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "层级新增失败");
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="物料目录"
        description="四位物料编码：级别 + 类别 + 品名 + 规格配置；分类阈值按 SOP 校验，实际采购单价在入库/验收时登记。"
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => materialsQuery.refetch()}
              disabled={materialsQuery.isFetching}
            >
              <RefreshCw
                className={materialsQuery.isFetching ? "h-4 w-4 animate-spin" : "h-4 w-4"}
              />
              刷新
            </Button>
            {canManage ? (
              <>
                <Button variant="outline" onClick={() => setImportOpen(true)}>
                  批量导入
                </Button>
                <Button onClick={openCreate}>
                  <Plus className="h-4 w-4" />
                  新建物料
                </Button>
              </>
            ) : null}
          </>
        }
      />

      <Tabs value={tab} onValueChange={(value) => setTab(value as "materials" | "taxonomy")}>
        <TabsList>
          <TabsTrigger value="materials">物料目录</TabsTrigger>
          <TabsTrigger value="taxonomy">层级维护</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "materials" ? (
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle>物料列表</CardTitle>
              <CardDescription>按编码或名称搜索；资产类物料必须选择规格配置。</CardDescription>
            </div>
            <div className="relative w-full max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="搜索物料编码或名称"
                className="pl-9"
              />
            </div>
          </CardHeader>
          <CardContent>
            {materialsQuery.isLoading ? (
              <div className="space-y-2">
                {["s1", "s2", "s3", "s4", "s5", "s6"].map((key) => (
                  <Skeleton key={key} className="h-11 w-full" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <EmptyState
                title="还没有物料"
                description="先在“层级维护”中建立类别、品名和规格配置，再新建物料。"
                action={canTaxonomy ? { label: "去层级维护", to: "/materials" } : undefined}
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>物料编码</TableHead>
                    <TableHead>名称</TableHead>
                    <TableHead>级别</TableHead>
                    <TableHead>类别 / 品名</TableHead>
                    <TableHead>规格配置</TableHead>
                    <TableHead>单位</TableHead>
                    <TableHead>预估单价</TableHead>
                    <TableHead>状态</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono text-xs">{row.code}</TableCell>
                      <TableCell className="font-medium">{row.name}</TableCell>
                      <TableCell>{levelById.get(row.levelId)?.name ?? "—"}</TableCell>
                      <TableCell>
                        {categoryById.get(row.categoryId ?? "")?.name ?? "—"} /{" "}
                        {productById.get(row.productId ?? "")?.name ?? "—"}
                      </TableCell>
                      <TableCell>
                        {specById.get(row.specConfigId ?? "")?.name ?? "未选择"}
                      </TableCell>
                      <TableCell>{row.unit}</TableCell>
                      <TableCell>
                        {row.estimatedUnitPriceCents === null
                          ? "未填写"
                          : `¥${(row.estimatedUnitPriceCents / 100).toLocaleString("zh-CN")}`}
                      </TableCell>
                      <TableCell>
                        <Badge variant={row.status === "active" ? "success" : "secondary"}>
                          {row.status === "active" ? "启用" : "停用"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {canManage ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingMaterial(row);
                              setEditForm({
                                name: row.name,
                                unit: row.unit,
                                price:
                                  row.estimatedUnitPriceCents === null
                                    ? ""
                                    : String(row.estimatedUnitPriceCents / 100),
                                lifeDays: String(row.expectedLifeDays),
                                companyId: row.companyId ?? "",
                                overrideReason: row.classificationOverrideReason ?? "",
                              });
                            }}
                          >
                            编辑
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 xl:grid-cols-[1fr_1.4fr]">
          <Card>
            <CardHeader>
              <CardTitle>新增层级</CardTitle>
              <CardDescription>编码由系统按可用编码自动生成；停用记录仍占用编码。</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>级别</Label>
                <select
                  value={taxonomyLevelId}
                  onChange={(event) => setTaxonomyLevelId(event.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
                >
                  <option value="">请选择级别</option>
                  {levels.map((level) => (
                    <option key={level.id} value={level.id}>
                      {level.code} · {level.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <Input
                  value={categoryName}
                  onChange={(event) => setCategoryName(event.target.value)}
                  placeholder="类别名称，例如：电脑"
                />
                <Button
                  variant="outline"
                  onClick={() => addTaxonomy("category")}
                  disabled={!canTaxonomy}
                >
                  新增类别
                </Button>
              </div>
              <div className="space-y-2">
                <Label>类别</Label>
                <select
                  value={taxonomyCategoryId}
                  onChange={(event) => setTaxonomyCategoryId(event.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
                >
                  <option value="">请选择类别</option>
                  {categories
                    .filter((item) => !taxonomyLevelId || item.levelId === taxonomyLevelId)
                    .map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.code} · {category.name}
                      </option>
                    ))}
                </select>
              </div>
              <div className="flex gap-2">
                <Input
                  value={productName}
                  onChange={(event) => setProductName(event.target.value)}
                  placeholder="品名名称，例如：笔记本电脑"
                />
                <Button
                  variant="outline"
                  onClick={() => addTaxonomy("product")}
                  disabled={!canTaxonomy}
                >
                  新增品名
                </Button>
              </div>
              <div className="space-y-2">
                <Label>品名</Label>
                <select
                  value={taxonomyProductId}
                  onChange={(event) => setTaxonomyProductId(event.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
                >
                  <option value="">请选择品名</option>
                  {products
                    .filter((item) => !taxonomyCategoryId || item.categoryId === taxonomyCategoryId)
                    .map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.code} · {product.name}
                      </option>
                    ))}
                </select>
              </div>
              <div className="flex gap-2">
                <Input
                  value={specName}
                  onChange={(event) => setSpecName(event.target.value)}
                  placeholder="规格配置，例如：14 英寸 / 32G / 1T"
                />
                <Button
                  variant="outline"
                  onClick={() => addTaxonomy("spec")}
                  disabled={!canTaxonomy}
                >
                  新增规格
                </Button>
              </div>
              {!canTaxonomy ? (
                <p className="text-xs text-muted-foreground">当前账号没有层级维护权限。</p>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>层级概览</CardTitle>
              <CardDescription>级别固定为 5 级；类别、品名、规格按父级维护。</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {levels.map((level) => (
                  <Badge key={level.id} variant="outline">
                    {level.code} · {level.name} ·{" "}
                    {categories.filter((item) => item.levelId === level.id).length} 类
                  </Badge>
                ))}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-border p-3">
                  <p className="text-xs font-medium text-muted-foreground">类别</p>
                  <p className="mt-1 text-2xl font-semibold">{categories.length}</p>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <p className="text-xs font-medium text-muted-foreground">品名</p>
                  <p className="mt-1 text-2xl font-semibold">{products.length}</p>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <p className="text-xs font-medium text-muted-foreground">规格配置</p>
                  <p className="mt-1 text-2xl font-semibold">{specs.length}</p>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <p className="text-xs font-medium text-muted-foreground">物料</p>
                  <p className="mt-1 text-2xl font-semibold">{materialsQuery.data?.length ?? 0}</p>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                {levels.map((level) => (
                  <div key={level.id} className="rounded-lg bg-muted/50 p-3">
                    <p className="font-medium">
                      {level.code} · {level.name}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {categories
                        .filter((item) => item.levelId === level.id)
                        .map((item) => item.name)
                        .join("、") || "暂无类别"}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog
        open={Boolean(editingMaterial)}
        onOpenChange={(open) => !open && setEditingMaterial(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>编辑物料：{editingMaterial?.code}</DialogTitle>
            <DialogDescription>层级/类别/品名/规格不可改；编码不变。</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>名称</Label>
              <Input
                value={editForm.name}
                onChange={(event) => setEditForm({ ...editForm, name: event.target.value })}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>单位</Label>
                <Input
                  value={editForm.unit}
                  onChange={(event) => setEditForm({ ...editForm, unit: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>单价（元）</Label>
                <Input
                  type="number"
                  value={editForm.price}
                  onChange={(event) => setEditForm({ ...editForm, price: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>预计使用期（天）</Label>
                <Input
                  type="number"
                  value={editForm.lifeDays}
                  onChange={(event) => setEditForm({ ...editForm, lifeDays: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>公司</Label>
                <select
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={editForm.companyId}
                  onChange={(event) => setEditForm({ ...editForm, companyId: event.target.value })}
                >
                  <option value="">不限定</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>分类越阈值原因（如适用）</Label>
              <Input
                value={editForm.overrideReason}
                onChange={(event) =>
                  setEditForm({ ...editForm, overrideReason: event.target.value })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingMaterial(null)}>
              取消
            </Button>
            <Button
              onClick={async () => {
                if (!editingMaterial) return;
                try {
                  await updateResource(`/api/v1/materials/${editingMaterial.id}`, {
                    name: editForm.name,
                    unit: editForm.unit,
                    estimatedUnitPriceYuan: editForm.price === "" ? null : Number(editForm.price),
                    expectedLifeDays: Number(editForm.lifeDays),
                    companyId: editForm.companyId || null,
                    classificationOverrideReason: editForm.overrideReason || null,
                  });
                  await Promise.all([
                    queryClient.invalidateQueries({ queryKey: ["materials"] }),
                    queryClient.invalidateQueries({ queryKey: ["reference-data"] }),
                  ]);
                  toast.success("物料已更新");
                  setEditingMaterial(null);
                } catch (error) {
                  toast.error(error instanceof Error ? error.message : "更新失败");
                }
              }}
            >
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>新建物料</DialogTitle>
            <DialogDescription>
              物料编码预览：
              <span className="font-mono text-foreground">{previewCode || "待选择层级"}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label>物料名称</Label>
              <Input
                value={form.name}
                onChange={(event) => setField("name", event.target.value)}
                placeholder="例如：ThinkPad X1 Carbon"
              />
            </div>
            <div className="space-y-2">
              <Label>物料级别</Label>
              <select
                value={form.levelId}
                onChange={(event) => setField("levelId", event.target.value)}
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
              >
                <option value="">请选择</option>
                {levels.map((level) => (
                  <option key={level.id} value={level.id}>
                    {level.code} · {level.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>物料类别</Label>
              <select
                value={form.categoryId}
                onChange={(event) => setField("categoryId", event.target.value)}
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
              >
                <option value="">请选择</option>
                {formCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.code} · {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>品名</Label>
              <select
                value={form.productId}
                onChange={(event) => setField("productId", event.target.value)}
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
              >
                <option value="">请选择</option>
                {formProducts.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.code} · {product.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>规格配置{formLevel?.code === 1 ? "（资产必选）" : "（选填）"}</Label>
              <select
                value={form.specConfigId}
                onChange={(event) => setField("specConfigId", event.target.value)}
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
              >
                <option value="">未选择</option>
                {formSpecs.map((spec) => (
                  <option key={spec.id} value={spec.id}>
                    {spec.code} · {spec.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>单位</Label>
              <Input value={form.unit} onChange={(event) => setField("unit", event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>预估单价（元）</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.price}
                onChange={(event) => setField("price", event.target.value)}
                placeholder="用于分类与授权预估"
              />
            </div>
            <div className="space-y-2">
              <Label>预计使用期（天）</Label>
              <Input
                type="number"
                min="1"
                value={form.lifeDays}
                onChange={(event) => setField("lifeDays", event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>所属公司</Label>
              <select
                value={form.companyId}
                onChange={(event) => setField("companyId", event.target.value)}
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
              >
                <option value="">未选择</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
            {canOverride ? (
              <div className="space-y-2 sm:col-span-2">
                <Label>分类调整原因（越阈值时必填）</Label>
                <Input
                  value={form.overrideReason}
                  onChange={(event) => setField("overrideReason", event.target.value)}
                  placeholder="仅管理单位指定人员可填写；原因将写入审计"
                />
              </div>
            ) : null}
            <div className="rounded-lg bg-muted/60 p-3 text-xs leading-5 text-muted-foreground sm:col-span-2">
              分类阈值：资产 ≥2000 元；低值易耗 500–2000 元或使用期 &gt;12 个月；耗材 &lt;500
              元且使用期 ≤12 个月；软件不按该三档拦截。
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              取消
            </Button>
            <Button onClick={createMaterial} disabled={saving}>
              {saving ? "保存中…" : "创建物料"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <CsvImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        dataType="material"
        onImported={() => {
          queryClient.invalidateQueries({ queryKey: ["materials"] });
          queryClient.invalidateQueries({ queryKey: ["reference-data"] });
        }}
      />
    </div>
  );
}
